import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Drawer, IconButton, Pagination, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import { ArrowLeft, ArrowRight, BookUser, Copy, Download, Mail, QrCode, Users, X } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import url from '../url';
import { findInvalidEmails, parseCsvEmails, parseEmailList } from '../utils/studentClassImport';
import { buildStudentClassInvitePath } from '../utils/studentClassRoutes';

const MAX_IMPORT_EMAILS = 500;
const MAX_CSV_SIZE = 1024 * 1024;

const panelColor = '#111b33';
const cardColor = '#17243f';
const fieldSx = {
  '& .MuiInputLabel-root': { color: '#aab9db' },
  '& .MuiInputBase-input': { color: '#e8efff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#34476d' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6075ad' }
};

function authConfig() {
  const token = localStorage.getItem('jwtToken');
  return {
    withCredentials: true,
    headers: token ? { Authorization: ['Bearer', token].join(' ') } : undefined
  };
}

export default function StudentManagementDrawer({ open, onClose }) {
  const { t } = useTranslation('homepage');
  const [view, setView] = useState('menu');
  const [className, setClassName] = useState('');
  const [emailsInput, setEmailsInput] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [createdClass, setCreatedClass] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [studentToRemove, setStudentToRemove] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const members = selectedClass?.members || [];
  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return members.filter((member) => (
      !query || member.name?.toLowerCase().includes(query) || member.email?.toLowerCase().includes(query)
    ));
  }, [members, search]);
  const pageSize = 8;
  const visibleMembers = filteredMembers.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredMembers.length / pageSize));
    if (page > pageCount) setPage(pageCount);
  }, [filteredMembers.length, page, pageSize]);

  useEffect(() => {
    if (!open) return;
    setView('menu');
    setError('');
    setNotice(null);
    setLoading(true);
    axios.get(`${url.backendHost}api/student-classes`, authConfig())
      .then((response) => {
        setClasses(response.data.classes || []);
      })
      .catch(() => setError(t('studentManagement.loadClassesFailed')))
      .finally(() => setLoading(false));
  }, [open, t]);

  useEffect(() => {
    setSelectedClass(null);
    setCreatedClass(null);
    setPage(1);
    if (open && view === 'members') {
      setLoading(true);
      axios.get(`${url.backendHost}api/student-classes`, authConfig())
        .then((response) => setClasses(response.data.classes || []))
        .catch(() => setError(t('studentManagement.loadClassesFailed')))
        .finally(() => setLoading(false));
    }
  }, [open, view, t]);

  const loadClasses = async () => {
    const response = await axios.get(`${url.backendHost}api/student-classes`, authConfig());
    setClasses(response.data.classes || []);
    return response.data.classes || [];
  };

  const startAction = async (nextView) => {
    setError('');
    setNotice(null);
    setView(nextView);
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv') || file.size > MAX_CSV_SIZE) {
      setError(file.size > MAX_CSV_SIZE
        ? t('studentManagement.csvTooLarge')
        : t('studentManagement.csvInvalid'));
      event.target.value = '';
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const emails = parseCsvEmails(String(reader.result || ''));
        setEmailsInput(emails.join('\n'));
        if (!emails.length) setError(t('studentManagement.csvNoEmails'));
      } catch {
        setError(t('studentManagement.csvReadFailed'));
      }
    };
    reader.onerror = () => setError(t('studentManagement.csvReadFailed'));
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImport = async (event) => {
    event.preventDefault();
    const emails = parseEmailList(emailsInput);
    const invalidEmails = findInvalidEmails(emails);
    if (!className.trim() || emails.length === 0) {
      setError(t('studentManagement.importRequired'));
      return;
    }
    if (emails.length > MAX_IMPORT_EMAILS) {
      setError(t('studentManagement.tooManyEmails', { count: MAX_IMPORT_EMAILS }));
      return;
    }
    if (invalidEmails.length) {
      setError(t('studentManagement.invalidEmails', { emails: invalidEmails.join(', ') }));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const classResponse = await axios.post(
        `${url.backendHost}api/student-classes`,
        { name: className.trim() },
        authConfig()
      );
      const response = await axios.post(
        `${url.backendHost}api/student-classes/${classResponse.data.id}/members/import`,
        { emails },
        authConfig()
      );
      setNotice({
        severity: response.data.notFound?.length ? 'warning' : 'success',
        text: response.data.notFound?.length
          ? t('studentManagement.importWithMissing', {
            added: response.data.added?.length || 0,
            already: response.data.alreadyMembers?.length || 0,
            missing: response.data.notFound.join(', ')
          })
          : t('studentManagement.importSuccess', {
            count: response.data.added?.length || 0,
            already: response.data.alreadyMembers?.length || 0
          })
      });
      setClassName('');
      setEmailsInput('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('studentManagement.importFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async (event) => {
    event.preventDefault();
    if (!className.trim()) {
      setError(t('studentManagement.classNameRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${url.backendHost}api/student-classes`,
        { name: className.trim() },
        authConfig()
      );
      setCreatedClass(response.data);
      setClassName('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('studentManagement.createClassFailed'));
    } finally {
      setLoading(false);
    }
  };

  const inviteUrl = createdClass
    ? `${window.location.origin}${buildStudentClassInvitePath(createdClass.inviteCode)}`
    : '';

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setNotice({ severity: 'success', text: t('studentManagement.linkCopied') });
    } catch {
      setError(t('studentManagement.copyFailed'));
    }
  };

  const handleDownloadQr = () => {
    const canvas = document.querySelector('[data-student-invite-qr]');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${createdClass.name}-invite.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const removeStudent = async () => {
    if (!studentToRemove || !selectedClass) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(
        `${url.backendHost}api/student-classes/${selectedClass.id}/members/${studentToRemove.id}`,
        authConfig()
      );
      const refreshedClasses = await loadClasses();
      const refreshedClass = refreshedClasses.find((classItem) => classItem.id === selectedClass.id);
      if (refreshedClass) setSelectedClass(refreshedClass);
      setNotice({ severity: 'success', text: t('studentManagement.studentRemoved') });
      setStudentToRemove(null);
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('studentManagement.removeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleClassJoining = async (classItem) => {
    setLoading(true);
    setError('');
    try {
      const joinEnabled = classItem.joinEnabled === false;
      const response = await axios.put(
        `${url.backendHost}api/student-classes/${classItem.id}/join-enabled`,
        { joinEnabled },
        authConfig()
      );
      setClasses((currentClasses) => currentClasses.map((item) => (
        item.id === classItem.id ? { ...item, joinEnabled: response.data.joinEnabled } : item
      )));
      setCreatedClass((currentClass) => (
        currentClass?.id === classItem.id
          ? { ...currentClass, joinEnabled: response.data.joinEnabled }
          : currentClass
      ));
      setNotice({
        severity: 'success',
        text: t(joinEnabled ? 'studentManagement.classJoiningEnabled' : 'studentManagement.classJoiningDisabled')
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('studentManagement.updateJoinSettingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const panelButton = {
    color: '#d9e5ff',
    borderColor: '#34476d',
    '&:hover': { borderColor: '#778cff', backgroundColor: 'rgba(112, 132, 255, .12)' }
  };
  const cardStyle = {
    background: `linear-gradient(145deg, ${cardColor}, #101a31)`,
    border: '1px solid #293b60',
    borderRadius: 3,
    color: '#e8efff'
  };

  const renderMenu = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#e8efff">
            <Users size={24} style={{ marginRight: 10, verticalAlign: 'middle', color: '#758cff' }} />
            {t('studentManagement.title')}
          </Typography>
          <Typography color="#aab9db" variant="body2" sx={{ mt: 0.5 }}>
            {t('studentManagement.description')}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => startAction('members')} sx={panelButton}>
          {t('studentManagement.viewClasses')} <ArrowRight size={16} style={{ marginLeft: 8 }} />
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {[
          { key: 'email', Icon: Mail, color: '#45b5ff', button: 'importButton' },
          { key: 'qr', Icon: QrCode, color: '#9a83ff', button: 'qrButton' },
          { key: 'members', Icon: BookUser, color: '#56d8c0', button: 'membersButton' }
        ].map(({ key, Icon, color, button }) => (
          <Card key={key} sx={{ ...cardStyle, minHeight: 250 }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Icon size={32} color={color} />
              <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                {t(`studentManagement.${key}Title`)}
              </Typography>
              <Typography variant="body2" color="#aab9db" sx={{ mt: 1, mb: 2 }}>
                {t(`studentManagement.${key}Description`)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => startAction(key)}
                sx={{ ...panelButton, mt: 'auto', alignSelf: 'flex-start' }}
              >
                {t(`studentManagement.${button}`)}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>
    </>
  );

  const renderEmail = () => (
    <Box component="form" onSubmit={handleImport} sx={{ maxWidth: 680 }}>
      <Typography variant="h6" color="#e8efff" fontWeight={700}>{t('studentManagement.emailTitle')}</Typography>
      <Typography variant="body2" color="#aab9db" sx={{ mt: 1 }}>{t('studentManagement.emailHint')}</Typography>
      <TextField
        fullWidth required label={t('studentManagement.className')} value={className}
        onChange={(event) => setClassName(event.target.value)} sx={{ ...fieldSx, mb: 2 }}
        inputProps={{ maxLength: 120 }}
      />
      <TextField
        fullWidth required multiline minRows={7} label={t('studentManagement.emailList')}
        placeholder="student1@example.com, student2@example.com"
        value={emailsInput} onChange={(event) => setEmailsInput(event.target.value)}
        sx={{ ...fieldSx, mb: 2 }}
      />
      <Button component="label" variant="outlined" sx={{ ...panelButton, mb: 2 }}>
        {t('studentManagement.uploadCsv')}
        <input hidden type="file" accept=".csv,text/csv" onChange={handleCsvUpload} />
      </Button>
      <Box><Button type="submit" variant="contained" disabled={loading}>{t('studentManagement.importButton')}</Button></Box>
    </Box>
  );

  const renderQr = () => (
    <Box component="form" onSubmit={handleCreateInvite} sx={{ maxWidth: 680 }}>
      <Typography variant="h6" color="#e8efff" fontWeight={700}>{t('studentManagement.qrTitle')}</Typography>
      <Typography variant="body2" color="#aab9db" sx={{ mt: 1 }}>{t('studentManagement.qrHint')}</Typography>
      {!createdClass ? (
        <>
          <TextField
            fullWidth required label={t('studentManagement.className')} value={className}
            onChange={(event) => setClassName(event.target.value)} sx={{ ...fieldSx, mb: 2 }}
            inputProps={{ maxLength: 120 }}
          />
          <Button type="submit" variant="contained" disabled={loading}>{t('studentManagement.createQr')}</Button>
        </>
      ) : (
        <Stack alignItems="center" spacing={2} sx={{ mt: 3 }}>
          <Typography variant="h6" color="#e8efff">{createdClass.name}</Typography>
          {createdClass.joinEnabled === false ? (
            <>
              <Alert severity="warning">{t('studentManagement.classJoiningDisabledMessage')}</Alert>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={() => toggleClassJoining(createdClass)}
                sx={panelButton}
              >
                {t('studentManagement.enableClassJoining')}
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2 }}>
                <QRCodeCanvas value={inviteUrl} size={220} data-student-invite-qr />
              </Box>
              <TextField fullWidth label={t('studentManagement.inviteLink')} value={inviteUrl} InputProps={{ readOnly: true }} sx={fieldSx} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" onClick={handleCopyInvite} startIcon={<Copy size={16} />} sx={panelButton}>
                  {t('studentManagement.copyLink')}
                </Button>
                <Button variant="outlined" onClick={handleDownloadQr} startIcon={<Download size={16} />} sx={panelButton}>
                  {t('studentManagement.downloadQr')}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={loading}
                  onClick={() => toggleClassJoining(createdClass)}
                  sx={panelButton}
                >
                  {t('studentManagement.disableClassJoining')}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Box>
  );

  const renderMembers = () => (
    <Box>
      <Typography variant="h6" color="#e8efff" fontWeight={700}>{t('studentManagement.membersTitle')}</Typography>
      {!selectedClass ? (
        <Stack spacing={1.5}>
          {classes.map((classItem) => (
            <Card key={classItem.id} sx={cardStyle}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
                <Button
                  onClick={() => { setSelectedClass(classItem); setSearch(''); setPage(1); }}
                  sx={{ ...panelButton, flex: 1, justifyContent: 'space-between', py: 1.5, textAlign: 'left' }}
                  variant="outlined"
                >
                  <Typography component="span" color="#e8efff">{classItem.name}</Typography>
                  <Typography component="span" color="#aab9db">{classItem.members?.length || 0} {t('studentManagement.students')}</Typography>
                </Button>
                <Stack alignItems="center" spacing={0.5}>
                  {classItem.joinEnabled !== false ? (
                    <>
                      <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, lineHeight: 0 }}>
                        <QRCodeCanvas
                          value={`${window.location.origin}${buildStudentClassInvitePath(classItem.inviteCode)}`}
                          size={104}
                          aria-label={t('studentManagement.classInviteQr', { name: classItem.name })}
                        />
                      </Box>
                      <Typography variant="caption" color="#aab9db">
                        {t('studentManagement.classInviteQrCaption')}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="#ffbd75" sx={{ textAlign: 'center', maxWidth: 120 }}>
                      {t('studentManagement.classJoiningDisabledLabel')}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    color={classItem.joinEnabled === false ? 'success' : 'warning'}
                    disabled={loading}
                    onClick={() => toggleClassJoining(classItem)}
                  >
                    {t(classItem.joinEnabled === false
                      ? 'studentManagement.enableClassJoining'
                      : 'studentManagement.disableClassJoining')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
          {!loading && classes.length === 0 && (
            <Typography color="#aab9db">{t('studentManagement.noClasses')}</Typography>
          )}
        </Stack>
      ) : (
        <>
          <Button onClick={() => setSelectedClass(null)} startIcon={<ArrowLeft size={16} />} sx={{ ...panelButton, mb: 2 }}>
            {t('studentManagement.allClasses')}
          </Button>
          <Typography variant="subtitle1" color="#e8efff" fontWeight={600} sx={{ mb: 1 }}>{selectedClass.name}</Typography>
          <TextField
            fullWidth size="small" label={t('studentManagement.searchMembers')} value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }} sx={{ ...fieldSx, mb: 2 }}
          />
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ color: '#aab9db', borderColor: '#293b60' }}>{t('studentManagement.name')}</TableCell>
                <TableCell sx={{ color: '#aab9db', borderColor: '#293b60' }}>{t('studentManagement.email')}</TableCell>
                <TableCell sx={{ color: '#aab9db', borderColor: '#293b60' }}>{t('studentManagement.joinedAt')}</TableCell>
                <TableCell sx={{ color: '#aab9db', borderColor: '#293b60' }}>{t('studentManagement.status')}</TableCell>
                <TableCell sx={{ color: '#aab9db', borderColor: '#293b60' }}>{t('studentManagement.actions')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {visibleMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell sx={{ color: '#e8efff', borderColor: '#293b60' }}>{member.name || member.email}</TableCell>
                    <TableCell sx={{ color: '#e8efff', borderColor: '#293b60' }}>{member.email}</TableCell>
                    <TableCell sx={{ color: '#e8efff', borderColor: '#293b60' }}>
                      {member.StudentClassMember?.joinedAt
                        ? new Date(member.StudentClassMember.joinedAt).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#e8efff', borderColor: '#293b60' }}>
                      {member.StudentClassMember?.status
                        ? t(`studentManagement.memberStatus.${member.StudentClassMember.status}`, {
                          defaultValue: member.StudentClassMember.status
                        })
                        : t('studentManagement.memberStatus.connected')}
                    </TableCell>
                    <TableCell sx={{ borderColor: '#293b60' }}>
                      <Button size="small" color="error" onClick={() => setStudentToRemove(member)}>
                        {t('studentManagement.remove')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!visibleMembers.length && <TableRow><TableCell colSpan={5} sx={{ color: '#aab9db', borderColor: '#293b60' }}>{t('studentManagement.noMembers')}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
          <Pagination
            count={Math.max(1, Math.ceil(filteredMembers.length / pageSize))}
            page={page}
            onChange={(_, nextPage) => setPage(nextPage)}
            sx={{ mt: 2, '.MuiPaginationItem-root': { color: '#d9e5ff' } }}
          />
        </>
      )}
    </Box>
  );

  const renderActiveView = () => {
    if (view === 'menu') return renderMenu();
    return (
      <>
        <Button
          onClick={() => { setView('menu'); setError(''); setNotice(null); }}
          startIcon={<ArrowLeft size={16} />}
          sx={{ ...panelButton, mb: 2 }}
        >
          {t('studentManagement.back')}
        </Button>
        {view === 'email' && renderEmail()}
        {view === 'qr' && renderQr()}
        {view === 'members' && renderMembers()}
      </>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 720, lg: 900 }, maxWidth: '100%', p: { xs: 2, sm: 4 }, bgcolor: panelColor } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton aria-label={t('studentManagement.close')} onClick={onClose} sx={{ color: '#aab9db' }}>
            <X size={20} />
          </IconButton>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
        {loading && <CircularProgress size={24} sx={{ position: 'absolute', top: 24, left: 24, color: '#8796ff' }} />}
        {classes.length === 0 && !loading && view === 'menu' ? (
          <>
            {renderActiveView()}
            <Typography color="#aab9db" variant="body2" sx={{ mt: 2 }}>{t('studentManagement.noClasses')}</Typography>
          </>
        ) : renderActiveView()}
      </Drawer>
      <Dialog open={Boolean(studentToRemove)} onClose={() => setStudentToRemove(null)}>
        <DialogTitle>{t('studentManagement.confirmRemoveTitle')}</DialogTitle>
        <DialogContent>{t('studentManagement.confirmRemove', { name: studentToRemove?.name || studentToRemove?.email })}</DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentToRemove(null)}>{t('studentManagement.cancel')}</Button>
          <Button color="error" onClick={removeStudent} disabled={loading}>{t('studentManagement.remove')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
